import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Surface, Text, Button, useTheme } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { requestMagicLink } from '@/src/api/apiService';

// Passwordless "check your email" confirmation screen.
//
// Reached from the login screen after a magic-link request. Web route:
// /magic-link-sent?email=... — the (auth) route group is stripped from the URL.
//
// The copy stays neutral (no account enumeration): we never confirm whether the
// email maps to a real account. Resend re-hits the request endpoint (backend is
// rate-limited to 1/min per email, so a rapid resend is silently throttled but
// still returns 200). Never a dead-end (DESIGN.md): resend + back-to-login.

const normalizeParam = (value: string | string[] | undefined): string => {
  if (Array.isArray(value)) {
    return value[0] || '';
  }
  return value || '';
};

export default function MagicLinkSentScreen() {
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation('login');
  const params = useLocalSearchParams<{ email?: string | string[] }>();
  const email = normalizeParam(params.email).trim();

  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleResend = async () => {
    if (!email || resending) return;
    setResending(true);
    setResent(false);
    try {
      // Response is always generic; we intentionally ignore ok/!ok here so the
      // neutral guidance stays identical whether or not the account exists.
      await requestMagicLink(email);
      setResent(true);
    } catch {
      // Non-fatal: keep the neutral guidance visible.
    } finally {
      setResending(false);
    }
  };

  const body = email
    ? t('magicLink.sentBody', { email })
    : t('magicLink.sentBodyNoEmail');

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.center}>
        <Surface style={styles.content} elevation={1}>
          <MaterialCommunityIcons
            name="email-fast-outline"
            size={48}
            color={theme.colors.primary}
            style={styles.icon}
            accessibilityElementsHidden
            importantForAccessibility="no"
          />
          <Text testID="magic-link-sent-title" variant="headlineSmall" style={styles.title}>
            {t('magicLink.sentTitle')}
          </Text>
          <Text
            testID="magic-link-sent-body"
            variant="bodyMedium"
            style={[styles.description, { color: theme.colors.onSurfaceVariant }]}
          >
            {body}
          </Text>
          {resent && (
            <Text
              testID="magic-link-resent"
              variant="bodySmall"
              style={[styles.description, { color: theme.colors.primary }]}
            >
              {t('magicLink.resent')}
            </Text>
          )}
          {!!email && (
            <Button
              mode="contained-tonal"
              onPress={handleResend}
              loading={resending}
              disabled={resending}
              style={styles.button}
              contentStyle={styles.buttonContent}
              icon="email-sync-outline"
              testID="magic-link-resend"
              accessibilityLabel={t('magicLink.resend')}
            >
              {t('magicLink.resend')}
            </Button>
          )}
          <Button
            mode="text"
            onPress={() => router.replace('/')}
            style={styles.button}
            contentStyle={styles.buttonContent}
            testID="magic-link-back"
            accessibilityLabel={t('magicLink.backToLogin')}
          >
            {t('magicLink.backToLogin')}
          </Button>
        </Surface>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    padding: 16,
  },
  content: {
    borderRadius: 8,
    padding: 24,
    alignItems: 'center',
  },
  icon: {
    marginBottom: 12,
  },
  title: {
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  description: {
    marginBottom: 16,
    textAlign: 'center',
  },
  button: {
    marginTop: 8,
    alignSelf: 'stretch',
  },
  buttonContent: {
    minHeight: 44,
  },
});
